import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

/**
 * 혜택 열람 직후 마케팅(광고성 정보) 수신 동의 제안 — 2026-10-05 대표:
 *   "필수만 동의하고 선택(마케팅)을 자꾸 빼고 로그인한다 → 만회·유도할 방법".
 * 로그인 화면에선 혜택을 보기 전이라 '왜 받아야 하는지'가 없다. 혜택을 본 바로 그 자리에서
 * "이 현장 조건이 바뀌면 알려드릴까요?"로 한 번만 묻는다(동의는 버튼을 누를 때만 — 미리 체크·강요 없음).
 * - 로그인 + 카카오 회원 + 아직 미동의일 때만, '괜찮아요'를 누르면 14일 동안 다시 묻지 않는다.
 * - 동의 저장은 마이페이지 스위치와 같은 API(PATCH /auth/kakao/consent/). 철회도 마이페이지에서.
 */
const DISMISS_KEY = 'mkt_optin_dismissed_at';
const DISMISS_DAYS = 14;

function dismissedRecently(): boolean {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return at > 0 && Date.now() - at < DISMISS_DAYS * 86400 * 1000;
  } catch {
    return false;
  }
}

export const BenefitAlertOptIn: React.FC<{ propertyTitle?: string }> = ({ propertyTitle }) => {
  const { isAuthenticated } = useAuth();
  const [state, setState] = useState<'hidden' | 'ask' | 'saving' | 'done'>('hidden');

  useEffect(() => {
    if (!isAuthenticated || dismissedRecently()) return;
    let cancelled = false;
    api
      .get('/auth/kakao/profile/')
      .then((r) => {
        if (!cancelled && r.data?.kakao_user && !r.data.kakao_user.consent_marketing) setState('ask');
      })
      .catch(() => {
        /* 카카오 회원이 아니거나 조회 실패 — 묻지 않는다 */
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  if (state === 'hidden') return null;

  if (state === 'done') {
    return (
      <div role="status" className="mt-2 rounded-ca-3xl border border-accent-line bg-accent-soft px-4 py-3 text-[14px] font-semibold text-accent-dark">
        알림을 켰어요. 새 할인·혜택이 나오면 가장 먼저 알려드릴게요. (마이페이지에서 언제든 끌 수 있어요)
      </div>
    );
  }

  const accept = async () => {
    setState('saving');
    try {
      const r = await api.patch('/auth/kakao/consent/', { consent_marketing: true });
      setState(r.data?.kakao_user?.consent_marketing === false ? 'ask' : 'done');
    } catch {
      setState('ask');
      alert('설정 저장에 실패했습니다. 잠시 후 다시 시도해주세요.');
    }
  };
  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* 저장 불가 — 이번 화면에서만 숨김 */
    }
    setState('hidden');
  };

  return (
    <div data-benefit-optin="" className="mt-2 flex flex-col gap-3 rounded-ca-3xl border border-accent-line bg-accent-soft p-4">
      <div className="flex flex-col gap-1">
        <b className="break-keep text-[16px] font-bold leading-[22px] text-ink">
          🔔 {propertyTitle ? `${propertyTitle} ` : '이 현장 '}할인 조건이 바뀌면 알려드릴까요?
        </b>
        <span className="break-keep text-[13px] leading-[19px] text-body">
          알림을 켠 회원에게는 <b>공개 전 잔여세대·할인율 변경</b>을 카카오톡·문자·전화로 가장 먼저 알려드려요. 마이페이지에서 언제든 끌 수 있어요.
        </span>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={accept}
          disabled={state === 'saving'}
          className="flex h-11 flex-1 items-center justify-center rounded-ca-xl bg-accent text-[15px] font-bold text-white active:opacity-80 disabled:opacity-60"
        >
          {state === 'saving' ? '저장 중…' : '알림 받기'}
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="h-11 rounded-ca-xl border border-ca-line bg-frame px-4 text-[14px] font-semibold text-muted active:opacity-80"
        >
          괜찮아요
        </button>
      </div>
      <span className="text-[11.5px] leading-[16px] text-muted">
        &lsquo;알림 받기&rsquo;를 누르면 [선택] 마케팅(광고성 정보) 수신에 동의하게 됩니다.{' '}
        <a href="/privacy#marketing" className="underline">
          내용 보기
        </a>
      </span>
    </div>
  );
};

export default BenefitAlertOptIn;
